import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
} from "react";
import {
  Paper,
  Box,
  Typography,
  Stack,
  Button,
  TextField,
  Chip,
  CircularProgress,
} from "@mui/material";
import useApi from "../../hooks/useApi";
import usePage from "../../hooks/usePage";
import { buildSpacesImageUrl } from "../../utils/spacesImageUrl";
import QAImageGallery from "./QAImageGallery";
const QADetail = forwardRef(({ answer, onUpdate, embedded = false }, ref) => {
  const { postData, uploadFile, retrieveFile } = useApi();
  const { showMessage } = usePage();
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState(answer?.qaComments || "");
  const [updatedAnswer, setUpdatedAnswer] = useState(answer);

  // Update when answer prop changes
  useEffect(() => {
    setUpdatedAnswer(answer);
    setComments(answer?.qaComments || "");
  }, [answer]);

  const handleApproveAllPending = async () => {
    try {
      setLoading(true);

      const imageKeys = (updatedAnswer?.imageApprovals || [])
        .map((approval) => approval.imageKey)
        .filter(Boolean);

      if (imageKeys.length === 0) {
        return;
      }

      for (const imageKey of imageKeys) {
        await postData({
          entityName: "SurveyAnswer",
          action: "approveImage",
          data: {
            surveyAnswerId: updatedAnswer?.id,
            imageKey,
            status: "approved",
          },
        });
      }

      onUpdate();
    } catch (err) {
      console.error("Failed to approve images:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlockImages = async () => {
    try {
      setLoading(true);
      await postData({
        entityName: "SurveyAnswer",
        action: "unlockImages",
        data: {
          surveyAnswerId: updatedAnswer?.id,
        },
      });
      onUpdate();
    } catch (err) {
      console.error("Failed to unlock images:", err);
    } finally {
      setLoading(false);
    }
  };

  // Expose methods to parent for keyboard shortcuts
  useImperativeHandle(
    ref,
    () => ({
      approveAllPending: handleApproveAllPending,
      unlockImages: handleUnlockImages,
    }),
    [handleApproveAllPending, handleUnlockImages],
  );

  const handleApproveImage = async (imageKey) => {
    try {
      setLoading(true);
      await postData({
        entityName: "SurveyAnswer",
        action: "approveImage",
        data: {
          surveyAnswerId: updatedAnswer.id,
          imageKey,
          status: "approved",
        },
      });
      onUpdate();
    } catch (err) {
      console.error("Failed to approve image:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUnapproveImage = async (imageKey) => {
    try {
      setLoading(true);
      await postData({
        entityName: "SurveyAnswer",
        action: "approveImage",
        data: {
          surveyAnswerId: updatedAnswer.id,
          imageKey,
          status: "unapproved",
        },
      });
      onUpdate();
    } catch (err) {
      console.error("Failed to unapprove image:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleReviewFlag = async (imageKey, reviewRequested) => {
    try {
      setLoading(true);
      await postData({
        entityName: "SurveyAnswer",
        action: "updateImageReviewFlag",
        data: {
          surveyAnswerId: updatedAnswer.id,
          imageKey,
          reviewRequested,
        },
      });
      onUpdate();
    } catch (err) {
      console.error("Failed to update review flag:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceImage = async (imageIndex, imageKey, oldFileName) => {
    if (loading) return;

    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = false;

    input.onchange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        await replaceImageWithFile({
          imageIndex,
          oldFileName,
          file,
          operationLabel: "handleReplaceImage",
        });
      } catch (err) {
        console.error("Failed to replace image:", err);
        showMessage(`Failed to replace image: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    input.click();
  };

  const replaceImageWithFile = async ({
    imageIndex,
    oldFileName,
    file,
    operationLabel,
  }) => {
    setLoading(true);

    const normalizedOldFileName =
      typeof oldFileName === "string" && oldFileName.trim()
        ? oldFileName.trim()
        : null;
    const uploadKey = normalizedOldFileName || crypto.randomUUID();

    console.log(
      `[${operationLabel}] Starting upload for image at index:`,
      imageIndex,
    );
    console.log(`[${operationLabel}] Old filename:`, oldFileName);
    console.log(`[${operationLabel}] Upload key:`, uploadKey);
    console.log(
      `[${operationLabel}] Question type:`,
      updatedAnswer.questionType,
    );

    const uploadResult = await uploadFile(file, {
      folder: "survey-images",
      key: uploadKey,
    });

    console.log(`[${operationLabel}] Upload result:`, uploadResult);

    if (!uploadResult?.url) {
      console.error("Upload failed - response:", uploadResult);
      throw new Error(
        uploadResult?.message || "Failed to upload image to storage",
      );
    }

    const newImageFileName =
      normalizedOldFileName ||
      uploadResult?.fileName ||
      (uploadResult?.fullKey
        ? String(uploadResult.fullKey).split("/").pop()
        : null) ||
      (uploadResult?.key ? String(uploadResult.key).split("/").pop() : null) ||
      String(uploadKey);

    const replaceResult = await postData({
      entityName: "SurveyAnswer",
      action: "replaceImage",
      data: {
        surveyAnswerId: updatedAnswer.id,
        imageIndex,
        oldImageFileName: oldFileName,
        newImageFileName,
        questionType: updatedAnswer.questionType,
      },
    });

    console.log(`[${operationLabel}] Replace result:`, replaceResult);

    if (!replaceResult?.isSuccessful) {
      console.error(
        `[${operationLabel}] Replace was not successful:`,
        replaceResult,
      );
      throw new Error(
        replaceResult?.message || "Failed to replace image in database",
      );
    }

    onUpdate();
    setLoading(false);
  };

  const imageUrlToBlob = async (imageKey) => {
    let resolvedUrl = null;

    try {
      resolvedUrl = await retrieveFile(imageKey);
    } catch (err) {
      console.warn(
        "[handleRotateImage] retrieveFile failed, falling back",
        err,
      );
    }

    const imageUrl =
      resolvedUrl ||
      buildSpacesImageUrl(imageKey, {
        defaultFolder: "survey-images",
      });

    const response = await fetch(imageUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch image: ${response.status}`);
    }

    return response.blob();
  };

  const rotateBlob = async (blob, degrees) => {
    const url = URL.createObjectURL(blob);

    try {
      const image = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = url;
      });

      const normalized = ((degrees % 360) + 360) % 360;
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) {
        throw new Error("Canvas context unavailable");
      }

      if (normalized === 90 || normalized === 270) {
        canvas.width = image.height;
        canvas.height = image.width;
      } else {
        canvas.width = image.width;
        canvas.height = image.height;
      }

      if (normalized === 90) {
        context.translate(canvas.width, 0);
        context.rotate(Math.PI / 2);
      } else if (normalized === 180) {
        context.translate(canvas.width, canvas.height);
        context.rotate(Math.PI);
      } else if (normalized === 270) {
        context.translate(0, canvas.height);
        context.rotate(-Math.PI / 2);
      }

      context.drawImage(image, 0, 0);

      const outputType =
        typeof blob?.type === "string" && blob.type.startsWith("image/")
          ? blob.type
          : "image/jpeg";

      const rotatedBlob = await new Promise((resolve) => {
        canvas.toBlob(
          (result) => resolve(result),
          outputType,
          outputType === "image/jpeg" ? 0.92 : undefined,
        );
      });

      if (!rotatedBlob) {
        throw new Error("Failed to create rotated image");
      }

      return rotatedBlob;
    } finally {
      URL.revokeObjectURL(url);
    }
  };

  const handleRotateImage = async (imageIndex, imageKey, degrees) => {
    if (loading || !imageKey) return;

    try {
      setLoading(true);
      const sourceBlob = await imageUrlToBlob(imageKey);
      const rotatedBlob = await rotateBlob(sourceBlob, degrees);

      const rotatedFile = new File([rotatedBlob], `rotated-${Date.now()}.jpg`, {
        type: rotatedBlob.type || "image/jpeg",
      });

      await replaceImageWithFile({
        imageIndex,
        oldFileName: imageKey,
        file: rotatedFile,
        operationLabel: "handleRotateImage",
      });
    } catch (err) {
      console.error("Failed to rotate image:", err);
      showMessage(`Failed to rotate image: ${err.message}`);
      setLoading(false);
    }
  };

  const handleUpdateComments = async () => {
    try {
      setLoading(true);
      await postData({
        entityName: "SurveyAnswer",
        action: "updateQAComments",
        data: {
          surveyAnswerId: updatedAnswer.id,
          qaComments: comments,
        },
      });
      onUpdate();
    } catch (err) {
      console.error("Failed to update comments:", err);
    } finally {
      setLoading(false);
    }
  };

  const images = useMemo(() => {
    const value = updatedAnswer?.value;
    if (!value) return [];

    // Standard image question: value is an array
    if (Array.isArray(value)) {
      return value;
    }

    // beforeAfter or distribution: value is an object
    if (typeof value === "object") {
      const result = [];

      // For beforeAfter: before/after arrays
      if (Array.isArray(value.before)) {
        result.push(...value.before);
      }
      if (Array.isArray(value.after)) {
        result.push(...value.after);
      }

      // For distribution: entryPhoto/actionPhoto arrays
      if (Array.isArray(value.entryPhoto)) {
        result.push(...value.entryPhoto);
      }
      if (Array.isArray(value.actionPhoto)) {
        result.push(...value.actionPhoto);
      }

      return result;
    }

    return [];
  }, [updatedAnswer]);
  const imageApprovals = updatedAnswer?.imageApprovals || [];

  const fullAddress = useMemo(() => {
    const address = updatedAnswer?.locationAddress;
    if (!address || typeof address !== "object") return "N/A";

    const line1 = [address.streetAddress, address.otherAddress]
      .filter(Boolean)
      .join(", ");
    const line2 = [address.city, address.province, address.postal]
      .filter(Boolean)
      .join(", ");

    return [line1, line2].filter(Boolean).join(" | ") || "N/A";
  }, [updatedAnswer]);

  return (
    <Paper
      sx={{
        p: embedded ? 0 : 3,
        height: embedded ? "auto" : "100%",
        display: "flex",
        flexDirection: "column",
        overflow: embedded ? "visible" : "hidden",
        boxShadow: embedded ? "none" : undefined,
      }}
    >
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Scrollable content */}
      <Box
        sx={{ flex: 1, overflow: embedded ? "visible" : "auto", minHeight: 0 }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Paper sx={{ p: 2, mb: 3 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
              >
                QA Context
              </Typography>
              <Typography variant="body2">
                <strong>Rep:</strong> {updatedAnswer?.repName || "Unknown Rep"}
              </Typography>
              {updatedAnswer?.controllingQuestionName ? (
                <Typography variant="body2">
                  <strong>Controlling Question:</strong>{" "}
                  {updatedAnswer.controllingQuestionName}
                </Typography>
              ) : null}
              <Typography variant="body2">
                <strong>Question:</strong>{" "}
                {updatedAnswer?.questionName || "N/A"}
              </Typography>
              {updatedAnswer?.skuName && (
                <Typography variant="body2">
                  <strong>SKU:</strong> {updatedAnswer.skuName}
                </Typography>
              )}
              {updatedAnswer?.skuUpc && (
                <Typography variant="body2">
                  <strong>UPC:</strong> {updatedAnswer.skuUpc}
                </Typography>
              )}
              {updatedAnswer?.skuPackSize && (
                <Typography variant="body2">
                  <strong>Pack Size:</strong> {updatedAnswer.skuPackSize}
                </Typography>
              )}
              {updatedAnswer?.skuClientCode && (
                <Typography variant="body2">
                  <strong>Client Code:</strong> {updatedAnswer.skuClientCode}
                </Typography>
              )}
              <Typography variant="body2">
                <strong>Project:</strong> {updatedAnswer?.projectName || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Province:</strong>{" "}
                {updatedAnswer?.locationProvince || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Date:</strong> {updatedAnswer?.scheduleDate || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Images:</strong> {images.length}
              </Typography>
            </Paper>

            {/* Location Information */}
            <Paper sx={{ p: 2, mb: 3, backgroundColor: "action.hover" }}>
              <Typography
                variant="subtitle2"
                gutterBottom
              >
                Location Information
              </Typography>
              <Typography variant="body2">
                <strong>Name:</strong> {updatedAnswer?.locationName || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Location #:</strong>{" "}
                {updatedAnswer?.locationNumber || "N/A"}
              </Typography>
              <Typography variant="body2">
                <strong>Full Address:</strong> {fullAddress}
              </Typography>
            </Paper>

            {/* Comments */}
            <Box sx={{ mb: 3 }}>
              <TextField
                label="QA Comments (Optional)"
                multiline
                rows={3}
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                fullWidth
                size="small"
                placeholder="Supervisor notes..."
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleUpdateComments}
                sx={{ mt: 1 }}
                disabled={loading}
              >
                Save Comments
              </Button>
            </Box>

            {/* Help */}
            <Paper sx={{ p: 2, backgroundColor: "action.hover" }}>
              <Typography variant="caption">
                <strong>Keyboard Shortcuts:</strong>
                <br />
                ↑/↓ - Navigate answers | A - Approve all pending | U - Unlock
                <br />
                <strong>Mouse:</strong> Click approve/unapprove buttons for
                individual image control
              </Typography>
            </Paper>
          </Box>

          {/* Image Gallery */}
          <Box sx={{ minWidth: 0, overflowX: "hidden" }}>
            <Typography
              variant="subtitle2"
              gutterBottom
            >
              Images ({images.length})
            </Typography>
            <QAImageGallery
              images={images}
              imageApprovals={imageApprovals}
              onApprove={handleApproveImage}
              onUnapprove={handleUnapproveImage}
              onToggleReviewFlag={handleToggleReviewFlag}
              onReplace={handleReplaceImage}
              onRotate={handleRotateImage}
              answer={updatedAnswer}
              questionType={updatedAnswer?.questionType}
            />
          </Box>
        </Box>
      </Box>

      {/* Action Buttons - Fixed at bottom */}
      <Box
        sx={{ mt: 3, pt: 2, borderTop: "1px solid", borderColor: "divider" }}
      >
        <Typography
          variant="subtitle2"
          gutterBottom
        >
          Actions
        </Typography>
        <Stack
          direction="row"
          spacing={2}
        >
          <Button
            variant="contained"
            color="success"
            onClick={handleApproveAllPending}
            disabled={loading}
          >
            Approve All Pending (A)
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleUnlockImages}
            disabled={loading}
          >
            Unlock (U)
          </Button>
        </Stack>
      </Box>
    </Paper>
  );
});

QADetail.displayName = "QADetail";

export default QADetail;
